/**
 * Emerald Books — Sprint 2 full-fat data-entry pages.
 * Recurring invoices/bills, Bank reconciliation, Statements of Account.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Play, Pause, Trash2, RefreshCw, Send, FileUp, Wand2,
  ArrowRight, Zap, Repeat, Landmark, ScrollText, CheckCircle2, Circle,
  XCircle, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, unwrapList, errMsg } from './api';

const money = (n) => (Number(n) || 0).toFixed(2);
const emptyLine = () => ({ description: '', quantity: 1, unit_price: 0, account_id: '', tax_code: '' });

/* -------------------------------------------------------------------------- */
/* Recurring Invoices/Bills                                                    */
/* -------------------------------------------------------------------------- */
export const RecurringPage = () => {
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [busyRun, setBusyRun] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    doc_kind: 'invoice', contact_id: '', cadence: 'monthly', day_of_month: 1,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '', max_occurrences: '', reference: '', notes: '',
    lines: [emptyLine()], auto_send: false,
  });

  const load = async () => {
    const [r, c, a] = await Promise.all([
      booksClient.get('/recurring'),
      booksClient.get('/contacts'),
      booksClient.get('/ledger/accounts'),
    ]);
    setRows(r.data);
    setContacts(unwrapList(c));
    setAccounts(unwrapList(a));
  };
  useEffect(() => { load(); }, []);

  const filteredContacts = useMemo(
    () => contacts.filter((c) => c.type === (form.doc_kind === 'invoice' ? 'customer' : 'vendor')),
    [contacts, form.doc_kind],
  );
  const filteredAccounts = useMemo(
    () => accounts.filter((a) => a.type === (form.doc_kind === 'invoice' ? 'income' : 'expense')),
    [accounts, form.doc_kind],
  );

  const updateLine = (i, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        max_occurrences: form.max_occurrences ? parseInt(form.max_occurrences, 10) : null,
        end_date: form.end_date || null,
        lines: form.lines.filter((l) => l.description && l.account_id),
      };
      if (!payload.lines.length) { toast.error('Add at least one line'); return; }
      await booksClient.post('/recurring', payload);
      toast.success('Template created');
      setShowForm(false);
      setForm({ ...form, lines: [emptyLine()] });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const run = async () => {
    setBusyRun(true);
    try {
      const { data } = await booksClient.post('/recurring/run');
      toast.success(`${data.created} draft document(s) generated for ${data.as_of}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusyRun(false); }
  };
  const pauseT = async (id) => { await booksClient.post(`/recurring/${id}/pause`); load(); };
  const resumeT = async (id) => { await booksClient.post(`/recurring/${id}/resume`); load(); };
  const delT = async (id) => {
    if (!confirm('Delete this template?')) return;
    await booksClient.delete(`/recurring/${id}`); load();
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="books-recurring-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Recurring Invoices &amp; Bills</h1>
          <p className="mt-1 text-sm text-slate-500">Auto-generate drafts on a schedule. Run manually or via cron.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={run} disabled={busyRun} data-testid="recurring-run-now">
            {busyRun ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Zap className="mr-1 h-4 w-4" />} Run due
          </Button>
          <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="recurring-new-btn">
            <Plus className="mr-1 h-4 w-4" /> New template
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 space-y-3 rounded-xl border bg-white p-4" data-testid="recurring-form">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Kind</div>
              <select value={form.doc_kind} onChange={(e) => setForm({ ...form, doc_kind: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm" data-testid="recurring-kind">
                <option value="invoice">Sales Invoice</option>
                <option value="bill">Supplier Bill</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">{form.doc_kind === 'invoice' ? 'Customer' : 'Vendor'}</div>
              <select required value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm" data-testid="recurring-contact">
                <option value="">Select…</option>
                {filteredContacts.map((c) => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Cadence</div>
              <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm" data-testid="recurring-cadence">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Start date</div>
              <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="recurring-start" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">End date (optional)</div>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Max occurrences</div>
              <Input type="number" min="1" placeholder="Unlimited" value={form.max_occurrences} onChange={(e) => setForm({ ...form, max_occurrences: e.target.value })} />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Reference</div>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
            </label>
          </div>

          <div>
            <div className="mb-1 text-xs font-medium text-slate-600">Lines</div>
            {form.lines.map((l, i) => (
              <div key={i} className="mb-2 grid grid-cols-12 gap-2">
                <Input className="col-span-4" placeholder="Description" value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} data-testid={`rec-line-desc-${i}`} />
                <Input className="col-span-1" type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: parseFloat(e.target.value) || 0 })} />
                <Input className="col-span-2" type="number" step="0.01" placeholder="Unit price" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: parseFloat(e.target.value) || 0 })} data-testid={`rec-line-price-${i}`} />
                <select className="col-span-4 rounded-md border px-2 text-sm" value={l.account_id} onChange={(e) => updateLine(i, { account_id: e.target.value })} data-testid={`rec-line-account-${i}`}>
                  <option value="">Account…</option>
                  {filteredAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
                </select>
                <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-rose-600 hover:text-rose-700" disabled={form.lines.length === 1}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addLine} className="text-sm text-emerald-600 hover:underline" data-testid="rec-add-line">+ Add line</button>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="recurring-save">Save template</Button>
          </div>
        </form>
      )}

      <div className="rounded-xl border bg-white">
        {rows.length === 0 ? <div className="p-8 text-center text-slate-400">No recurring templates yet.</div> :
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">Kind</th>
                <th className="p-3 text-left">Contact</th>
                <th className="p-3 text-left">Cadence</th>
                <th className="p-3 text-left">Next run</th>
                <th className="p-3 text-right">Runs</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const contact = contacts.find((c) => c.contact_id === t.contact_id);
                return (
                  <tr key={t.template_id} className="border-b last:border-b-0" data-testid={`rec-row-${t.template_id}`}>
                    <td className="p-3 capitalize">{t.doc_kind}</td>
                    <td className="p-3">{contact?.name || t.contact_id.slice(0, 8)}</td>
                    <td className="p-3 capitalize">{t.cadence}</td>
                    <td className="p-3">{t.next_run_date}</td>
                    <td className="p-3 text-right">{t.runs_count}{t.max_occurrences ? ` / ${t.max_occurrences}` : ''}</td>
                    <td className="p-3">
                      <span className={'rounded-full px-2 py-0.5 text-xs ' + (
                        t.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        t.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-500'
                      )}>{t.status}</span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {t.status === 'active' && <button onClick={() => pauseT(t.template_id)} className="text-slate-500 hover:text-amber-600" data-testid={`rec-pause-${t.template_id}`}><Pause className="h-4 w-4" /></button>}
                        {t.status === 'paused' && <button onClick={() => resumeT(t.template_id)} className="text-slate-500 hover:text-emerald-600"><Play className="h-4 w-4" /></button>}
                        <button onClick={() => delT(t.template_id)} className="text-slate-500 hover:text-rose-600" data-testid={`rec-del-${t.template_id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Bank Reconciliation                                                         */
/* -------------------------------------------------------------------------- */
export const BankReconPage = () => {
  const [statements, setStatements] = useState([]);
  const [openStmt, setOpenStmt] = useState(null);
  const [lines, setLines] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [uploadBank, setUploadBank] = useState('');
  const [file, setFile] = useState(null);
  const [posting, setPosting] = useState(null); // line_id currently posting
  const [quickPost, setQuickPost] = useState({ open: false, line: null, counter: '', memo: '' });

  const loadStmts = () => booksClient.get('/bank-recon/statements').then((r) => setStatements(r.data));
  const loadAccounts = () => booksClient.get('/ledger/accounts').then((r) => {
    const list = unwrapList(r);
    setAccounts(list);
    setBankAccounts(list.filter((a) => a.subtype === 'bank' || a.subtype === 'cash'));
  });
  useEffect(() => { loadStmts(); loadAccounts(); }, []);

  const openStatement = async (s) => {
    setOpenStmt(s);
    const { data } = await booksClient.get(`/bank-recon/statements/${s.statement_id}/lines`);
    setLines(data);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !uploadBank) { toast.error('Pick a bank account + a CSV file'); return; }
    const form = new FormData();
    form.append('file', file);
    form.append('bank_account_id', uploadBank);
    try {
      const { data } = await booksClient.post('/bank-recon/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${data.rows_imported} rows`);
      setFile(null); setUploadBank('');
      loadStmts();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const autoMatch = async () => {
    if (!openStmt) return;
    try {
      const { data } = await booksClient.post(`/bank-recon/auto-match/${openStmt.statement_id}`);
      toast.success(`${data.matched} lines matched, ${data.unmatched_remaining} remaining`);
      openStatement(openStmt);
      loadStmts();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doQuickPost = async () => {
    if (!quickPost.counter) { toast.error('Pick a counter account'); return; }
    setPosting(quickPost.line.statement_line_id);
    try {
      await booksClient.post('/bank-recon/quick-post', {
        statement_line_id: quickPost.line.statement_line_id,
        counter_account_id: quickPost.counter, memo: quickPost.memo,
      });
      toast.success('Posted + reconciled');
      setQuickPost({ open: false, line: null, counter: '', memo: '' });
      openStatement(openStmt);
      loadStmts();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setPosting(null); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="books-bank-recon-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Bank Reconciliation</h1>
        <p className="mt-1 text-sm text-slate-500">Import a bank statement CSV, auto-match to ledger, quick-post the rest.</p>
      </div>

      {!openStmt && (
        <>
          {/* Import form */}
          <form onSubmit={upload} className="mb-6 rounded-xl border bg-white p-4" data-testid="bankrec-import-form">
            <div className="mb-3 text-sm font-medium text-slate-700">Import statement</div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <div className="mb-1 text-xs text-slate-500">Bank account</div>
                <select required value={uploadBank} onChange={(e) => setUploadBank(e.target.value)} className="rounded-md border px-2 py-1.5 text-sm min-w-[220px]" data-testid="bankrec-account">
                  <option value="">Select…</option>
                  {bankAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
                </select>
              </label>
              <label className="text-sm">
                <div className="mb-1 text-xs text-slate-500">CSV file</div>
                <input type="file" accept=".csv,text/csv" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="bankrec-file" />
              </label>
              <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="bankrec-upload-btn">
                <FileUp className="mr-1 h-4 w-4" /> Upload
              </Button>
            </div>
            <div className="mt-2 text-xs text-slate-400">Columns: Date · Description · Amount (or Debit/Credit)</div>
          </form>

          {/* Statements list */}
          <div className="rounded-xl border bg-white">
            {statements.length === 0 ? <div className="p-8 text-center text-slate-400">No statements imported yet.</div> :
              <table className="w-full text-sm">
                <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="p-3 text-left">Imported</th>
                    <th className="p-3 text-left">Filename</th>
                    <th className="p-3 text-right">Lines</th>
                    <th className="p-3 text-right">Matched</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((s) => (
                    <tr key={s.statement_id} className="border-b last:border-b-0" data-testid={`stmt-row-${s.statement_id}`}>
                      <td className="p-3">{(s.created_at || '').slice(0, 10)}</td>
                      <td className="p-3">{s.filename}</td>
                      <td className="p-3 text-right">{s.total_lines}</td>
                      <td className="p-3 text-right">{s.matched_lines} / {s.total_lines}</td>
                      <td className="p-3 text-right"><button onClick={() => openStatement(s)} className="text-emerald-600 hover:underline" data-testid={`stmt-open-${s.statement_id}`}>Open →</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </div>
        </>
      )}

      {openStmt && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <button onClick={() => { setOpenStmt(null); setLines([]); }} className="text-sm text-slate-500 hover:underline">← Back to statements</button>
            <Button onClick={autoMatch} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="bankrec-auto-btn">
              <Wand2 className="mr-1 h-4 w-4" /> Auto-match
            </Button>
          </div>
          <div className="rounded-xl border bg-white">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.statement_line_id} className="border-b last:border-b-0" data-testid={`bank-line-${l.statement_line_id}`}>
                    <td className="p-3">{(l.date || '').slice(0, 10)}</td>
                    <td className="p-3">{l.description}</td>
                    <td className={'p-3 text-right ' + (l.amount < 0 ? 'text-rose-600' : 'text-emerald-700')}>{money(l.amount)}</td>
                    <td className="p-3">
                      {l.status === 'matched'
                        ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> matched</span>
                        : <span className="inline-flex items-center gap-1 text-slate-500"><Circle className="h-3.5 w-3.5" /> unmatched</span>}
                    </td>
                    <td className="p-3 text-right">
                      {l.status === 'unmatched' && (
                        <button onClick={() => setQuickPost({ open: true, line: l, counter: '', memo: '' })} className="text-emerald-600 hover:underline" data-testid={`bank-quickpost-${l.statement_line_id}`}>
                          Quick-post →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick post modal */}
      {quickPost.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bankrec-quickpost-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Post journal for line</h2>
              <button onClick={() => setQuickPost({ open: false, line: null, counter: '', memo: '' })} className="text-slate-400 hover:text-slate-700"><XCircle className="h-5 w-5" /></button>
            </div>
            <div className="mb-4 rounded bg-slate-50 p-3 text-sm">
              <div><strong>{quickPost.line.description}</strong></div>
              <div className="text-slate-500">{(quickPost.line.date || '').slice(0, 10)} · {money(quickPost.line.amount)}</div>
            </div>
            <label className="block text-sm">
              <div className="mb-1 text-xs text-slate-500">Counter account (e.g. Bank charges, Interest income)</div>
              <select value={quickPost.counter} onChange={(e) => setQuickPost({ ...quickPost, counter: e.target.value })} className="w-full rounded-md border px-2 py-2 text-sm" data-testid="bankrec-counter-select">
                <option value="">Select…</option>
                {accounts.filter((a) => ['expense', 'income'].includes(a.type)).map((a) => (
                  <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>
                ))}
              </select>
            </label>
            <label className="mt-3 block text-sm">
              <div className="mb-1 text-xs text-slate-500">Memo (optional)</div>
              <Input value={quickPost.memo} onChange={(e) => setQuickPost({ ...quickPost, memo: e.target.value })} data-testid="bankrec-memo" />
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setQuickPost({ open: false, line: null, counter: '', memo: '' })}>Cancel</Button>
              <Button disabled={posting === quickPost.line?.statement_line_id} onClick={doQuickPost} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="bankrec-post-btn">
                {posting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Zap className="mr-1 h-4 w-4" />} Post + match
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Statements                                                                  */
/* -------------------------------------------------------------------------- */
export const StatementsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [kind, setKind] = useState('customer');
  const [contactId, setContactId] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const ninetyAgo = new Date(Date.now() - 90 * 86400 * 1000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: ninetyAgo, end: today });
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => { booksClient.get('/contacts').then((r) => setContacts(unwrapList(r))); }, []);
  const filteredContacts = useMemo(() => contacts.filter((c) => c.type === kind), [contacts, kind]);

  const fetchStmt = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data } = await booksClient.get(`/statements/${kind}/${contactId}`, {
        params: { start_date: range.start, end_date: range.end },
      });
      setStmt(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  const send = async () => {
    if (!contactId) return;
    setSending(true);
    try {
      const { data } = await booksClient.post('/statements/send', {
        contact_id: contactId, kind,
        start_date: range.start, end_date: range.end,
      });
      toast.success(`Sent to ${data.sent_to}`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSending(false); }
  };

  const runReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await booksClient.post('/statements/reminders/run?dry_run=false');
      toast.success(`${data.sent} reminder(s) sent`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSendingReminders(false); }
  };

  return (
    <div className="mx-auto max-w-5xl" data-testid="books-statements-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Statements of Account</h1>
          <p className="mt-1 text-sm text-slate-500">Running-balance ledger per customer or vendor. Email to send.</p>
        </div>
        <Button variant="outline" onClick={runReminders} disabled={sendingReminders} data-testid="stmt-reminders-run">
          {sendingReminders ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
          Run payment reminders
        </Button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 rounded-xl border bg-white p-4 md:grid-cols-5" data-testid="stmt-controls">
        <label className="text-sm">
          <div className="mb-1 text-xs text-slate-500">Type</div>
          <select value={kind} onChange={(e) => { setKind(e.target.value); setContactId(''); setStmt(null); }} className="w-full rounded-md border px-2 py-1.5 text-sm" data-testid="stmt-kind">
            <option value="customer">Customer</option>
            <option value="vendor">Vendor</option>
          </select>
        </label>
        <label className="text-sm md:col-span-2">
          <div className="mb-1 text-xs text-slate-500">{kind === 'customer' ? 'Customer' : 'Vendor'}</div>
          <select value={contactId} onChange={(e) => setContactId(e.target.value)} className="w-full rounded-md border px-2 py-1.5 text-sm" data-testid="stmt-contact">
            <option value="">Select…</option>
            {filteredContacts.map((c) => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-slate-500">From</div>
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="stmt-from" />
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-slate-500">To</div>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="stmt-to" />
        </label>
        <div className="md:col-span-5 flex gap-2">
          <Button onClick={fetchStmt} disabled={!contactId || loading} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="stmt-view-btn">
            {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ScrollText className="mr-1 h-4 w-4" />} View
          </Button>
          <Button variant="outline" onClick={send} disabled={!stmt || sending} data-testid="stmt-send-btn">
            {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Email
          </Button>
        </div>
      </div>

      {stmt && (
        <div className="rounded-xl border bg-white" data-testid="stmt-result">
          <div className="border-b px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-500">Statement for</div>
                <div className="text-lg font-semibold">{stmt.contact?.name}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-slate-500">Total due</div>
                <div className={'text-2xl font-semibold ' + (stmt.total_due > 0 ? 'text-slate-900' : 'text-emerald-600')}>{money(stmt.total_due)}</div>
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Reference</th>
                <th className="p-3 text-right">Debit</th>
                <th className="p-3 text-right">Credit</th>
                <th className="p-3 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {stmt.rows.map((r, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="p-3">{r.date}</td>
                  <td className="p-3">{r.ref}</td>
                  <td className="p-3 text-right">{r.debit ? money(r.debit) : ''}</td>
                  <td className="p-3 text-right">{r.credit ? money(r.credit) : ''}</td>
                  <td className="p-3 text-right font-medium">{money(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
